import { Box, Transition } from '@mantine/core';
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

export interface RotateInProps {
  children: React.ReactNode;
  visible?: boolean;
  duration?: number;
  delay?: number;
  degrees?: number;
  direction?: 'clockwise' | 'counterclockwise';
  withScale?: boolean;
  timingFunction?: string;
  onEnter?: () => void;
  onEntered?: () => void;
  onExit?: () => void;
  onExited?: () => void;
}

const RotateIn = forwardRef<HTMLDivElement, RotateInProps>(
  (
    {
      children,
      visible = true,
      duration = 400,
      delay = 0,
      degrees = 90,
      direction = 'clockwise',
      withScale = true,
      timingFunction = 'ease-out',
      onEnter,
      onEntered,
      onExit,
      onExited,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const actualDuration = reducedMotion ? 0 : duration;
    const rotateValue = direction === 'clockwise' ? -degrees : degrees;

    const rotateTransition = {
      in: {
        opacity: 1,
        transform: `rotate(0deg)${withScale ? ' scale(1)' : ''}`,
      },
      out: {
        opacity: 0,
        transform: `rotate(${rotateValue}deg)${withScale ? ' scale(0.8)' : ''}`,
      },
      common: { transformOrigin: 'center center' },
      transitionProperty: 'opacity, transform',
    };

    return (
      <Transition
        mounted={visible}
        transition={rotateTransition}
        duration={actualDuration}
        timingFunction={timingFunction}
        onEnter={onEnter}
        onEntered={onEntered}
        onExit={onExit}
        onExited={onExited}
      >
        {(styles) => (
          <Box
            ref={ref}
            style={{
              ...styles,
              transitionDelay: `${delay}ms`,
            }}
          >
            {children}
          </Box>
        )}
      </Transition>
    );
  }
);

RotateIn.displayName = 'RotateIn';

export default RotateIn;
